import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ArrowLeft, Plus, Trash2, Upload, Settings, Loader2, Users, UserPlus, Edit, Truck, List } from 'lucide-react';
import { colors } from '../theme';
import TransportadoraManager from './TransportadoraManager';
import GerenciadorStatus from './GerenciadorStatus';

const AdminFinal = ({ user, defaultTab = 'config' }) => {
  // Estados principais
  const [transportadoras, setTransportadoras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(defaultTab);
  
  // Estados para modais
  const [showUserForm, setShowUserForm] = useState(false);
  const [showTranspForm, setShowTranspForm] = useState(false);
  const [editingTransp, setEditingTransp] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Formulários
  const [userForm, setUserForm] = useState({
    username: '',
    name: '',
    password: '',
    role: 'vendedor'
  });
  
  const [transpForm, setTranspForm] = useState({
    nome: '',
    url_rastreamento: ''
  });

  // Configuração do sistema
  const [systemConfig, setSystemConfig] = useState({
    excel_url: '',
    auto_refresh_active: false,
    auto_refresh_interval: 5
  });
  const [lastUpdate, setLastUpdate] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    await Promise.all([
      loadTransportadoras(),
      loadUsuarios(),
      loadSystemConfig()
    ]);
  };

  // === FUNÇÕES DE USUÁRIOS ===
  const loadUsuarios = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const saveUser = async () => {
    if (!userForm.username || !userForm.name) {
      alert('Preencha username e nome');
      return;
    }

    // Se estiver editando, senha é opcional (só atualiza se for preenchida)
    if (!editingUser && !userForm.password) {
      alert('Preencha a senha para criar novo usuário');
      return;
    }

    setLoading(true);
    try {
      if (editingUser) {
        // Modo edição - UPDATE
        const dataToUpdate = {
          username: userForm.username,
          name: userForm.name,
          role: userForm.role
        };
        
        // Só incluir senha se foi preenchida
        if (userForm.password && userForm.password.trim()) {
          dataToUpdate.password = userForm.password;
        }
        
        await axios.put(`${API}/auth/users/${editingUser}`, dataToUpdate);
        alert('✅ Usuário atualizado com sucesso!');
      } else {
        // Modo criação - CREATE
        await axios.post(`${API}/auth/register`, userForm);
        alert('✅ Usuário criado com sucesso!');
      }
      
      resetUserForm();
      await loadUsuarios();
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao salvar usuário: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const resetUserForm = () => {
    setUserForm({ username: '', name: '', password: '', role: 'vendedor' });
    setEditingUser(null);
    setShowUserForm(false);
  };

  const editUser = (usuario) => {
    setUserForm({
      username: usuario.username,
      name: usuario.name,
      password: '', // Não pré-preencher senha por segurança
      role: usuario.role
    });
    setEditingUser(usuario.id);
    setShowUserForm(true);
  };

  const toggleUserActive = async (userId, currentStatus) => {
    try {
      // Implementar endpoint de update no backend seria ideal
      // Por enquanto, vamos simular
      alert(`Funcionalidade para ${currentStatus ? 'desativar' : 'ativar'} usuário será implementada no backend`);
      // await axios.put(`${API}/auth/users/${userId}`, { is_active: !currentStatus });
      // await loadUsuarios();
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao alterar status do usuário');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!confirm(`Tem certeza que deseja excluir o usuário ${userName}?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/auth/users/${userId}`);
      alert('✅ Usuário excluído com sucesso!');
      await loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('❌ Erro ao excluir usuário: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // === FUNÇÕES DE TRANSPORTADORAS ===
  const loadTransportadoras = async () => {
    try {
      const response = await axios.get(`${API}/transportadoras`);
      setTransportadoras(response.data);
    } catch (error) {
      console.error('Erro ao carregar transportadoras:', error);
    }
  };

  const saveTransportadora = async () => {
    if (!transpForm.nome || !transpForm.url_rastreamento) {
      alert('Preencha nome e URL de rastreamento');
      return;
    }

    setLoading(true);
    try {
      if (editingTransp) {
        await axios.put(`${API}/transportadoras/${editingTransp}`, transpForm);
      } else {
        await axios.post(`${API}/transportadoras`, { ...transpForm, ativa: true });
      }
      
      resetTranspForm();
      await loadTransportadoras();
      alert('✅ Transportadora salva com sucesso!');
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao salvar transportadora');
    } finally {
      setLoading(false);
    }
  };

  const deleteTransportadora = async (id) => {
    if (!confirm('Deletar esta transportadora?')) return;
    
    try {
      await axios.delete(`${API}/transportadoras/${id}`);
      await loadTransportadoras();
      alert('✅ Transportadora deletada!');
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao deletar transportadora');
    }
  };

  const editTransportadora = (transp) => {
    setTranspForm({
      nome: transp.nome,
      url_rastreamento: transp.url_rastreamento
    });
    setEditingTransp(transp.id);
    setShowTranspForm(true);
  };

  const resetTranspForm = () => {
    setTranspForm({ nome: '', url_rastreamento: '' });
    setEditingTransp(null);
    setShowTranspForm(false);
  };

  const toggleTranspActive = async (transpId, currentStatus) => {
    try {
      await axios.put(`${API}/transportadoras/${transpId}`, { ativa: !currentStatus });
      await loadTransportadoras();
      alert(`✅ Transportadora ${!currentStatus ? 'ativada' : 'desativada'} com sucesso!`);
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao alterar status da transportadora');
    }
  };

  // === FUNÇÕES DO SISTEMA ===
  const loadSystemConfig = async () => {
    try {
      const response = await axios.get(`${API}/config/system`);
      setSystemConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const updateSystemConfig = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/config/system`, systemConfig);
      alert('✅ Configuração salva! Todos os usuários verão as atualizações.');
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const testApiConnection = async () => {
    if (!systemConfig.api_url || !systemConfig.api_url.trim()) {
      alert('Configure a URL da API primeiro!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/test-external-api`, {
        url: systemConfig.api_url.trim(),
        token: systemConfig.api_token || ''
      });

      alert(`✅ Conexão com API bem-sucedida!\n\n📡 Status: ${response.data.status_code}\n💡 API está respondendo corretamente.`);
      
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao testar API: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const importFromApi = async () => {
    if (!systemConfig.api_url || !systemConfig.api_url.trim()) {
      alert('Configure a URL da API primeiro!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/import-from-api`);

      const now = new Date().toLocaleString();
      setLastUpdate(now);
      
      alert(`✅ Dados importados da API com sucesso!\n\n📊 Total de registros: ${response.data.total_records}\n✅ Importados: ${response.data.imported_records}\n\n💡 Todos os usuários já podem ver os novos dados!`);
      
    } catch (error) {
      console.error('Erro:', error);
      alert('❌ Erro ao importar da API: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              Painel de Administração
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gerencie configurações, usuários e transportadoras do sistema
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6">
        {/* Navigation */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1 shadow-sm border">
          {[
            { id: 'config', label: 'Configurações', icon: Settings },
            { id: 'status', label: 'Status Pedidos', icon: List },
            { id: 'usuarios', label: 'Usuários', icon: Users },
            { id: 'transportadoras', label: 'Transportadoras', icon: Truck }
          ].map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-6 py-3 rounded-md font-medium transition-all flex items-center gap-2 ${
                activeSection === section.id
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              style={{
                backgroundColor: activeSection === section.id ? colors.primaza.primary : 'transparent'
              }}
            >
              <section.icon size={18} />
              {section.label}
            </button>
          ))}
        </div>

        {/* SEÇÃO: CONFIGURAÇÕES */}
        {activeSection === 'config' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ color: colors.primaza.secondary }}>
                  🔗 Configuração de API Externa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>URL da API *</Label>
                  <Input
                    placeholder="https://api.exemplo.com/pedidos"
                    value={systemConfig.api_url || ''}
                    onChange={(e) => setSystemConfig({...systemConfig, api_url: e.target.value})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL completa da API que retorna os pedidos em formato JSON
                  </p>
                </div>
                
                <div>
                  <Label>Token de Autenticação (opcional)</Label>
                  <Input
                    type="password"
                    placeholder="Bearer token ou API key"
                    value={systemConfig.api_token || ''}
                    onChange={(e) => setSystemConfig({...systemConfig, api_token: e.target.value})}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Token será enviado no header Authorization
                  </p>
                </div>
                
                <div 
                  className="flex items-center space-x-4 p-4 rounded-lg border"
                  style={{
                    backgroundColor: colors.primaza.primaryLight,
                    borderColor: colors.primaza.primary
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={systemConfig.auto_refresh_active}
                      onCheckedChange={(checked) => setSystemConfig({...systemConfig, auto_refresh_active: checked})}
                    />
                    <Label>🔄 Auto-refresh global ativo</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Label>Intervalo:</Label>
                    <select 
                      value={systemConfig.auto_refresh_interval}
                      onChange={(e) => setSystemConfig({...systemConfig, auto_refresh_interval: parseInt(e.target.value)})}
                      className="px-3 py-1 border rounded"
                    >
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">💡 Formato esperado da API:</h4>
                  <pre className="text-xs text-yellow-700 bg-yellow-100 p-3 rounded overflow-x-auto">
{`[
  {
    "pedido": "39702",
    "vendedor": "GUILHERME LANG",
    "nfe": "12345",
    "chave_nfe": "43251106...",
    "cod_cliente": "10532",
    "nome_cliente": "NOME DO CLIENTE",
    "status_pedido": "FATURADO",
    "prev_entrega": "2025-11-20",
    "transportadora": "Correios",
    "cod_rastreamento": "AB123456BR",
    "valor_frete": "35.00"
  }
]`}
                  </pre>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={testApiConnection}
                    disabled={loading || !systemConfig.api_url}
                    style={{ 
                      borderColor: colors.primaza.primary,
                      color: colors.primaza.secondary 
                    }}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Settings className="h-4 w-4 mr-2" />
                    )}
                    🧪 Testar Conexão
                  </Button>
                  
                  <Button 
                    onClick={importFromApi} 
                    disabled={loading || !systemConfig.api_url}
                    style={{
                      backgroundColor: colors.primaza.primary,
                      color: 'white'
                    }}
                    className="hover:opacity-90"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    🔄 Importar Dados da API
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={updateSystemConfig}
                    disabled={loading}
                  >
                    💾 Salvar Configuração
                  </Button>
                </div>

                {lastUpdate && (
                  <div 
                    className="rounded-lg p-3 border"
                    style={{
                      backgroundColor: colors.primaza.primaryLight,
                      borderColor: colors.primaza.primary
                    }}
                  >
                    <p className="text-sm" style={{ color: colors.primaza.secondaryDark }}>
                      ✅ <strong>Última importação:</strong> {lastUpdate}
                      <br />
                      💡 Todos os usuários já podem ver os dados atualizados no Dashboard
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* SEÇÃO: USUÁRIOS */}
        {activeSection === 'usuarios' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>👥 Controle de Usuários ({usuarios.length})</span>
                  <Button onClick={() => setShowUserForm(!showUserForm)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {showUserForm ? 'Cancelar' : 'Novo Usuário'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Formulário de Usuário */}
                {showUserForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                    <h3 className="font-medium mb-4">
                      {editingUser ? 'Editar Usuário' : 'Criar Novo Usuário'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nome de Usuário (Login)</Label>
                        <Input
                          placeholder="ana.paula"
                          value={userForm.username}
                          onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Nome Completo</Label>
                        <Input
                          placeholder="Ana Paula Silva"
                          value={userForm.name}
                          onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Senha</Label>
                        <Input
                          type="password"
                          placeholder="Digite a senha"
                          value={userForm.password}
                          onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Perfil</Label>
                        <select 
                          value={userForm.role}
                          onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                          className="w-full px-3 py-2 border rounded"
                        >
                          <option value="vendedor">Vendedor</option>
                          <option value="lancador">Lançador</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={saveUser} disabled={loading}>
                        {loading ? 'Salvando...' : (editingUser ? 'Atualizar Usuário' : 'Criar Usuário')}
                      </Button>
                      <Button variant="outline" onClick={resetUserForm}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Lista de Usuários */}
                <div className="space-y-3">
                  {usuarios.map((usuario) => (
                    <div key={usuario.id} className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-gray-50">
                      <div className="space-y-1">
                        <div className="font-medium">{usuario.name}</div>
                        <div className="text-sm text-gray-500">
                          @{usuario.username} • Criado em {new Date(usuario.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={usuario.role === 'admin' ? 'default' : 'secondary'}>
                          {usuario.role === 'admin' ? '👑 Admin' : '👤 Vendedor'}
                        </Badge>
                        <Badge variant={usuario.is_active ? 'default' : 'destructive'}>
                          {usuario.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editUser(usuario)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteUser(usuario.id, usuario.name)}
                          className="text-red-600 hover:bg-red-50 border-red-300"
                          title="Excluir Usuário"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SEÇÃO: STATUS PEDIDOS */}
        {activeSection === 'status' && (
          <GerenciadorStatus />
        )}

        {/* SEÇÃO: TRANSPORTADORAS */}
        {activeSection === 'transportadoras' && (
          <TransportadoraManager />
        )}

        {/* SEÇÃO: TRANSPORTADORAS OLD (backup) */}
        {activeSection === 'transportadoras_old' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>🚚 Transportadoras ({transportadoras.length})</span>
                  <Button onClick={() => setShowTranspForm(!showTranspForm)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {showTranspForm ? 'Cancelar' : 'Nova Transportadora'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Formulário de Transportadora */}
                {showTranspForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                    <h3 className="font-medium mb-4">
                      {editingTransp ? 'Editar Transportadora' : 'Nova Transportadora'}
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Nome da Transportadora</Label>
                        <Input
                          placeholder="EXPRESSO SÃO MIGUEL"
                          value={transpForm.nome}
                          onChange={(e) => setTranspForm({...transpForm, nome: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>URL de Rastreamento</Label>
                        <Input
                          placeholder="https://site.com/rastreamento?codigo={{code}}"
                          value={transpForm.url_rastreamento}
                          onChange={(e) => setTranspForm({...transpForm, url_rastreamento: e.target.value})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use {'{{code}}'} para código e {'{{chave}}'} para chave NFe
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={saveTransportadora} disabled={loading}>
                        {loading ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button variant="outline" onClick={resetTranspForm}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Lista de Transportadoras */}
                <div className="space-y-3">
                  {transportadoras.map((transp) => (
                    <div key={transp.id} className="flex items-center justify-between p-4 border rounded-lg bg-white hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium">{transp.nome}</div>
                        <div className="text-sm text-gray-500 truncate max-w-md">
                          {transp.url_rastreamento}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={transp.ativa ? "default" : "secondary"}>
                          {transp.ativa ? 'Ativa' : 'Inativa'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => editTransportadora(transp)}
                          title="Editar transportadora"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleTranspActive(transp.id, transp.ativa)}
                          className={transp.ativa ? 'text-red-600' : 'text-green-600'}
                          title={transp.ativa ? 'Desativar' : 'Ativar'}
                        >
                          {transp.ativa ? '❌' : '✅'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteTransportadora(transp.id)}
                          className="text-red-600"
                          title="Deletar transportadora"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {transportadoras.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-4">Nenhuma transportadora cadastrada</p>
                      <Button onClick={() => setShowTranspForm(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Primeira Transportadora
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFinal;